/**
 * Deck Registry Service
 * 
 * DB-first deck discovery for searchable presentations.
 * 
 * Source of truth:
 * - Backend `/presentations/catalog` endpoint for deck list/titles/status
 * - Local Office context only for `CURRENT` anchor resolution
 * 
 * This preserves index-based mapping used by agents:
 * - numeric indexes (0, 1, 2) and aliases (`d0`, `d1`, ...)
 * - `CURRENT` always resolves from local active presentation context
 */

import { BackendClient, PresentationCatalogEntry } from '../communication/backendClient';
import { getOrCreatedocumentId } from '../documentIdManager';
import type { IDocumentRegistry } from '@carousel-office/agent-runtime';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Information about a registered presentation
 */
export interface DeckInfo {
    /** Deck ID (e.g., "deck_abc123") */
    id: string;
    /** Presentation title/filename */
    title: string;
    /** Number of slides ready to search (indexed) */
    slidesReady: number;
    /** Number of slides currently indexing */
    slidesIndexing: number;
    /** Number of slides currently retrying after failure */
    slidesRetrying: number;
    /** Number of slides currently marked failed */
    slidesFailed: number;
    /** Total number of slides in the presentation */
    totalSlides: number;
    /** Processing status: 'processing', 'complete', 'error' */
    status: 'processing' | 'complete' | 'error' | 'partial';
    /** Is this the currently active presentation? */
    isCurrent: boolean;
    /** When the deck was last processed */
    lastProcessed?: string;
}

/**
 * Deck registry mapping: index -> deck info
 */
export type DeckRegistryMap = Map<number, DeckInfo>;
type CatalogStatus = PresentationCatalogEntry['status'];
type NormalizedCatalogEntry = PresentationCatalogEntry & { status: CatalogStatus };

// ═══════════════════════════════════════════════════════════════════════════
// DECK REGISTRY CLASS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Deck Registry Service
 * 
 * Manages available presentations for search agents.
 * Provides index-based references for token-efficient LLM interactions.
 */
export class DeckRegistry implements IDocumentRegistry<DeckInfo> {
    private static instance: DeckRegistry;
    
    private backendClient: BackendClient;
    private _registry: DeckRegistryMap = new Map();
    private _currentdocumentId?: string;
    private _lastKnownCatalog: PresentationCatalogEntry[] = [];
    private _indexTodocumentId: Map<number, string> = new Map();
    private _documentIdToIndex: Map<string, number> = new Map();
    private _listeners: Set<() => void> = new Set();
    
    private constructor() {
        this.backendClient = new BackendClient();
    }
    
    /**
     * Get singleton instance
     */
    public static getInstance(): DeckRegistry {
        if (!DeckRegistry.instance) {
            DeckRegistry.instance = new DeckRegistry();
        }
        return DeckRegistry.instance;
    }
    
    /**
     * Initialize the registry
     */
    public async initialize(): Promise<void> {
        await this.refresh();
    }
    
    /**
     * Refresh registry from backend catalog (DB-first)
     */
    public async refresh(): Promise<void> {
        const previousSnapshot = JSON.stringify(this.toJSON());

        // Get current deck ID
        // First, check if it was passed from the parent window (for dialog context)
        const storeddocumentId = (globalThis as any).__currentdocumentId as string | undefined;
        
        if (storeddocumentId) {
            console.log('[DeckRegistry] Using stored current deck ID from window:', storeddocumentId);
            this._currentdocumentId = storeddocumentId;
        } else {
            // Try to get it from PowerPoint (works in taskpane context)
            try {
                this._currentdocumentId = await getOrCreatedocumentId();
                console.log('[DeckRegistry] Got current deck ID from PowerPoint:', this._currentdocumentId);
            } catch (error) {
                console.warn('[DeckRegistry] Could not get current deck ID:', error);
                this._currentdocumentId = undefined;
            }
        }

        // Catalog source is backend DB; if unavailable, use last-known in-memory cache.
        let catalogEntries: PresentationCatalogEntry[] = this._lastKnownCatalog;
        try {
            catalogEntries = await this.backendClient.getPresentationsCatalog();
            this._lastKnownCatalog = catalogEntries;
        } catch (error) {
            console.error('[DeckRegistry] Failed to fetch backend catalog:', error);
            if (this._lastKnownCatalog.length > 0) {
                console.warn(`[DeckRegistry] Using last-known catalog cache (${this._lastKnownCatalog.length} decks)`);
            } else {
                console.warn('[DeckRegistry] No catalog data available; registry will be empty except CURRENT anchor');
            }
        }

        const normalizedCatalog = this.normalizeCatalogEntries(catalogEntries);
        const catalogBydocumentId = new Map<string, NormalizedCatalogEntry>(
            normalizedCatalog.map((entry) => [entry.deck_id, entry])
        );

        const orderedEntries: Array<{ entry: NormalizedCatalogEntry; isCurrent: boolean }> = [];

        // Keep CURRENT anchored by local Office context even if catalog is stale/unavailable.
        if (this._currentdocumentId) {
            const currentEntry = catalogBydocumentId.get(this._currentdocumentId) ?? {
                deck_id: this._currentdocumentId,
                title: 'Current Presentation',
                slide_count: 0,
                status: 'opening',
            };
            orderedEntries.push({ entry: currentEntry, isCurrent: true });
            catalogBydocumentId.delete(this._currentdocumentId);
        }

        // Stable ordering contract for index mapping.
        const remainingEntries = Array.from(catalogBydocumentId.values()).sort((a, b) => {
            return a.deck_id.localeCompare(b.deck_id);
        });
        for (const entry of remainingEntries) {
            orderedEntries.push({ entry, isCurrent: false });
        }

        // Build registry from ordered catalog entries
        this._registry.clear();
        this._indexTodocumentId.clear();
        this._documentIdToIndex.clear();
        let nextIndex = 0;
        for (const item of orderedEntries) {
            const info = this.catalogEntryToDeckInfo(item.entry, item.isCurrent);
            this._registry.set(nextIndex, info);
            this._indexTodocumentId.set(nextIndex, item.entry.deck_id);
            this._documentIdToIndex.set(item.entry.deck_id, nextIndex);
            nextIndex += 1;
        }
        
        console.log('[DeckRegistry] Registered decks:', {
            totalDecks: this._registry.size,
            currentdocumentId: this._currentdocumentId,
            decks: Array.from(this._registry.entries()).map(([idx, info]) => ({
                index: idx,
                title: info.title,
                ready: `${info.slidesReady}/${info.totalSlides}`,
                id: info.id,
                status: info.status,
                isCurrent: info.isCurrent
            }))
        });

        const nextSnapshot = JSON.stringify(this.toJSON());
        if (previousSnapshot !== nextSnapshot) {
            this.emitChange();
        }
    }

    private normalizeCatalogStatus(status: string | undefined): CatalogStatus {
        const normalized = (status || 'processing').toLowerCase();
        if (
            normalized === 'opening' ||
            normalized === 'deferred' ||
            normalized === 'processing' ||
            normalized === 'partial' ||
            normalized === 'complete' ||
            normalized === 'error'
        ) {
            return normalized;
        }
        return 'processing';
    }

    private normalizeCatalogEntries(entries: PresentationCatalogEntry[]): NormalizedCatalogEntry[] {
        const deduped = new Map<string, NormalizedCatalogEntry>();
        for (const rawEntry of entries) {
            if (!rawEntry || !rawEntry.deck_id) {
                continue;
            }
            deduped.set(rawEntry.deck_id, {
                ...rawEntry,
                status: this.normalizeCatalogStatus(rawEntry.status),
            });
        }
        return Array.from(deduped.values());
    }

    private mapToDeckStatus(status: CatalogStatus): DeckInfo['status'] {
        if (status === 'complete' || status === 'partial' || status === 'error') {
            return status;
        }
        // opening/deferred/processing are grouped as processing from search-readiness perspective.
        return 'processing';
    }

    private catalogEntryToDeckInfo(entry: NormalizedCatalogEntry, isCurrent: boolean): DeckInfo {
        const totalSlides = Math.max(0, Number(entry.slide_count) || 0);
        const rawStatus = this.normalizeCatalogStatus(entry.status);

        let slidesReady = 0;
        let slidesIndexing = 0;
        let slidesRetrying = 0;
        let slidesFailed = 0;

        if (rawStatus === 'complete') {
            slidesReady = totalSlides;
        } else if (rawStatus === 'partial') {
            slidesReady = totalSlides > 0 ? totalSlides - 1 : 0;
            slidesFailed = totalSlides > 0 ? 1 : 0;
        } else if (rawStatus === 'error') {
            slidesFailed = totalSlides > 0 ? totalSlides : 1;
        } else if (rawStatus === 'deferred') {
            // Deferred indexing intentionally has no in-flight work until slide count grows.
            slidesIndexing = 0;
        } else {
            // opening / processing
            slidesIndexing = totalSlides;
        }

        return {
            id: entry.deck_id,
            title: entry.title || 'Untitled Presentation',
            slidesReady,
            slidesIndexing,
            slidesRetrying,
            slidesFailed,
            totalSlides,
            status: this.mapToDeckStatus(rawStatus),
            isCurrent,
            lastProcessed: entry.last_processed,
        };
    }
    
    /**
     * Get deck info by index
     */
    public getDeckByIndex(index: number): DeckInfo | undefined {
        return this._registry.get(index);
    }
    
    /**
     * Get deck info by ID
     */
    public getDeckById(documentId: string): DeckInfo | undefined {
        const index = this._documentIdToIndex.get(documentId);
        if (index !== undefined) {
            return this._registry.get(index);
        }
        return undefined;
    }
    
    /**
     * Resolve a deck reference (index, "CURRENT", "d0", or deck ID) to full deck ID
     * 
     * @param ref - Deck reference: number (0), string index ("0"), "CURRENT", "d0", or full deck ID
     * @returns Full deck ID (e.g., "deck_abc123-def456...")
     * 
     * @example
     * resolveDeckRef(0)           // → "deck_abc123..."
     * resolveDeckRef("d0")        // → "deck_abc123..."
     * resolveDeckRef("CURRENT")   // → current deck ID
     * resolveDeckRef("deck_abc")  // → "deck_abc" (pass-through)
     */
    public resolveDeckRef(ref: string | number): string | undefined {
        // Handle "CURRENT"
        if (typeof ref === 'string' && ref.toUpperCase() === 'CURRENT') {
            return this._currentdocumentId;
        }
        
        // Handle "d{index}" format (e.g., "d0", "d1")
        if (typeof ref === 'string' && /^d\d+$/.test(ref)) {
            const index = parseInt(ref.substring(1), 10);
            return this._indexTodocumentId.get(index);
        }
        
        // Handle numeric index
        if (typeof ref === 'number') {
            return this._indexTodocumentId.get(ref);
        }
        
        // Handle string index (e.g., "0", "1")
        const numRef = parseInt(ref, 10);
        if (!isNaN(numRef)) {
            return this._indexTodocumentId.get(numRef);
        }
        
        // Assume it's already a deck ID
        return ref;
    }
    
    /**
     * Get the registry index for a deck ID
     * 
     * @param documentId - Full deck ID
     * @returns Registry index or undefined if not found
     */
    public getDeckIndex(documentId: string): number | undefined {
        return this._documentIdToIndex.get(documentId);
    }
    
    /**
     * Get the short alias for a deck ID
     * 
     * @param documentId - Full deck ID
     * @returns Short alias like "d0", "d1", or undefined if not registered
     * 
     * @example
     * getDeckAlias("deck_abc123...")  // → "d0"
     */
    public getDeckAlias(documentId: string): string | undefined {
        const index = this._documentIdToIndex.get(documentId);
        return index !== undefined ? `d${index}` : undefined;
    }
    
    /**
     * Get the current deck ID
     */
    public getCurrentdocumentId(): string | undefined {
        return this._currentdocumentId;
    }

    /**
     * Backward-compatible alias used by agent-runtime context providers.
     */
    public getCurrentDocumentId(): string | undefined {
        return this.getCurrentdocumentId();
    }
    
    /**
     * Get the full registry map
     */
    public getRegistry(): DeckRegistryMap {
        return this._registry;
    }
    
    /**
     * Get all deck infos as an array
     */
    public getAllDecks(): DeckInfo[] {
        return Array.from(this._registry.values());
    }

    public getAll(): DeckInfo[] {
        return this.getAllDecks();
    }
    
    /**
     * Get registry as object for JSON serialization
     */
    public toJSON(): Record<number, DeckInfo> {
        const result: Record<number, DeckInfo> = {};
        for (const [idx, info] of this._registry) {
            result[idx] = info;
        }
        return result;
    }
    
    /**
     * Format registry for LLM consumption (non-user automated reminder format)
     */
    public formatForLLM(): string {
        if (this._registry.size === 0) {
            return 'No presentations currently available to search.';
        }
        
        const lines: string[] = [
            'Available Presentations to Search:',
        ];
        
        for (const [idx, info] of this._registry) {
            const statusIcon = info.status === 'complete' ? '✓' : 
                               info.status === 'partial' ? '◐' : 
                               info.status === 'processing' ? '⏳' : '✗';
            const currentMarker = info.isCurrent ? ' [CURRENT]' : '';
            lines.push(
                `  ${idx}: "${info.title}" - ${info.slidesReady}/${info.totalSlides} slides indexed ${statusIcon}${currentMarker}`
            );
        }
        
        lines.push('');
        lines.push('Use index number (0, 1, 2) or "CURRENT" for documentId parameter in searchPresentation.');
        
        return lines.join('\n');
    }
    
    /**
     * Update deck info (e.g., when slides finish processing)
     */
    public updateDeckInfo(documentId: string, updates: Partial<DeckInfo>): void {
        const index = this._documentIdToIndex.get(documentId);
        if (index !== undefined) {
            const current = this._registry.get(index);
            if (current) {
                this._registry.set(index, { ...current, ...updates });
                this.emitChange();
            }
        }
    }

    public subscribe(listener: () => void): () => void {
        this._listeners.add(listener);
        return () => {
            this._listeners.delete(listener);
        };
    }

    private emitChange(): void {
        for (const listener of this._listeners) {
            try {
                listener();
            } catch (error) {
                console.warn('[DeckRegistry] Listener threw error:', error);
            }
        }
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON ACCESSOR
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the DeckRegistry singleton instance
 */
export function getDeckRegistry(): DeckRegistry {
    return DeckRegistry.getInstance();
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default DeckRegistry;




